import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { media, size, spacing } from 'ui';
import { PopoverItem, PopoverItemButton, PopoverMenu } from 'ui/popover';
import Dropdown from './Dropdown';
import { getAgnosticUserPlaces, getSelectedPlace } from 'selectors';
import { selectPlace as selectPlaceAction } from 'actions/ui';
import get from 'lodash/get';
import { MdUnfoldMore } from 'react-icons/md';
import { Scrollbars } from 'react-custom-scrollbars';
import { history } from 'store';
import { getPaginatePlaces } from '../actions/places';
import { Spinner } from '@tymate/margaret';
import { capitalize } from '@tymate/sergic';

const Wrapper = styled.div`
   display: none;

   ${media.tablet`
    display: inline-block;
    width: 100%;

    button {
      width: 100%;
    }
  `};
`;

const PlaceTrigger = styled.div`
   padding: ${spacing(3 / 4)} ${spacing()};
   position: relative;
   width: 100%;
`;

const PlaceTriggerBody = styled.span`
   display: table-cell;
   vertical-align: middle;
`;

const PlaceTriggerName = styled.span`
   display: flex;
   color: ${({ theme }) => theme.sidebarLinkColor};
   padding-right: ${250 - 32 - 162}px;
   min-height: ${spacing(2.25)};
   align-items: center;
`;

const PlaceTriggerIconWrapper = styled.span`
   position: absolute;
   top: 50%;
   right: ${spacing(0.25)};
   transform: translateY(-50%);
   font-size: ${size(1)};
   line-height: 0.8;

   > svg {
      fill: #fff;
   }
`;

const PlaceImage = styled.div`
   position: absolute;
   background-image: url(${props => props.pictureUrl});
   background-size: cover;
   background-position: center center;
   width: 40px;
   height: 40px;
   border-radius: ${({ theme }) => theme.borderRadius};
   top: 50%;
   transform: translateY(-50%);
   right: ${spacing(1.5)};
`;

const PlaceSwitcherTrigger = ({ selectedPlace, hasManyPlaces }) => (
   <PlaceTrigger readOnly={!hasManyPlaces}>
      <PlaceTriggerBody>
         <PlaceTriggerName>{capitalize(get(selectedPlace, 'displayName') || '\u00A0')} </PlaceTriggerName>

         <PlaceImage pictureUrl={selectedPlace.pictureUrl} />

         {hasManyPlaces && (
            <PlaceTriggerIconWrapper>
               <MdUnfoldMore />
            </PlaceTriggerIconWrapper>
         )}
      </PlaceTriggerBody>
   </PlaceTrigger>
);

const PlaceSwitcher = () => {
   const dropdownRef = useRef();
   const dispatch = useDispatch();

   const { places, selectedPlace, pagination, isFetching } = useSelector(state => ({
      places: getAgnosticUserPlaces(state),
      selectedPlace: getSelectedPlace(state),
      pagination: state.places.placesPagination,
      isFetching: state.places.isFetching,
   }));

   const hasManyPlaces = (places || []).length > 1;

   const selectPlace = useCallback(id => dispatch(selectPlaceAction(id)), [dispatch]);

   const fetchPlaces = useCallback(page => dispatch(getPaginatePlaces(false, { page: page || 1 })), [dispatch]);

   const handleSelectPlace = id => {
      selectPlace(id);

      if (!dropdownRef.current) {
         return;
      }
      dropdownRef.current.close();
      history.push('/');
   };

   const handleLoadMore = top => {
      if (top > 0.95 && pagination?.nextPage) {
         fetchPlaces(pagination?.nextPage);
      }
   };

   useEffect(() => {
      fetchPlaces();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   if (places.length === 0) {
      return null;
   }

   return (
      <Wrapper>
         <Dropdown
            ref={dropdownRef}
            readOnly={!hasManyPlaces}
            trigger={<PlaceSwitcherTrigger selectedPlace={selectedPlace} hasManyPlaces={hasManyPlaces} />}
         >
            {hasManyPlaces && (
               <Scrollbars autoHeight autoHeightMax='350px' onScrollFrame={({ top }) => handleLoadMore(top)}>
                  <PopoverMenu>
                     {places?.filter(Boolean).map(({ id, displayName }) => (
                        <PopoverItem key={id}>
                           <PopoverItemButton onClick={() => handleSelectPlace(id)}>
                              {capitalize(displayName)}
                           </PopoverItemButton>
                        </PopoverItem>
                     ))}
                     {isFetching && <Spinner />}
                  </PopoverMenu>
               </Scrollbars>
            )}
         </Dropdown>
      </Wrapper>
   );
};

export default PlaceSwitcher;
